import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, UserPlus, Building2, Mail, MapPin } from 'lucide-react';
import customerService from '../services/customerService';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';

const ITEMS_PER_PAGE = 20;

const CustomerSearchModal = ({ onClose, onCustomerSelect, onAddNew, user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const currentQueryRef = useRef('');

  // Focus search input and load recent customers on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    loadCustomers('', 1, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomers = useCallback(async (query, page = 1, append = false) => {
    if (!user) return;
    const franchiseeId = getFranchiseeIdForDataLoading(user);
    if (!franchiseeId) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      let response;
      if (query.trim()) {
        response = await customerService.searchCustomers(franchiseeId, query, ITEMS_PER_PAGE, page);
      } else {
        // No search term — load most recently active customers
        response = await customerService.getCustomers(franchiseeId, {
          page,
          limit: ITEMS_PER_PAGE,
          sortBy: 'updated_at',
          sortOrder: 'desc'
        });
      }
      const newCustomers = response.customers || [];

      // Only apply results if the query hasn't changed while we were fetching
      if (query !== currentQueryRef.current) return;

      if (append) {
        setCustomers(prev => [...prev, ...newCustomers]);
      } else {
        setCustomers(newCustomers);
      }

      setTotalCount(response.total || newCustomers.length);
      setCurrentPage(page);
      setHasMore(page < (response.pages || 1));
    } catch (error) {
      console.error('Error searching customers:', error);
      if (!append) setCustomers([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user]);

  // Debounced search - reset to page 1
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    currentQueryRef.current = searchTerm;
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setHasMore(false);
      loadCustomers(searchTerm, 1, false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, loadCustomers]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadCustomers(searchTerm, currentPage + 1, true);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, searchTerm, currentPage, loadCustomers]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const formatAddress = (customer) => {
    const parts = [
      customer.address_line1,
      customer.address_city,
      customer.address_postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-start justify-center z-50 pt-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">Select Customer</h2>
            <p className="text-sm text-white/90 mt-1">
              {loading ? 'Searching...' : (
                `${totalCount} customer${totalCount !== 1 ? 's' : ''} found${customers.length < totalCount ? ` (showing ${customers.length})` : ''}`
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              <UserPlus size={18} />
              <span className="hidden sm:inline">Add New Customer</span>
              <span className="sm:hidden">New</span>
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors duration-200"
              aria-label="Close customer search"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search Field */}
        <div className="p-4 md:p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
              placeholder="Search by name, company, email, or postcode..."
              autoFocus
            />
          </div>
        </div>

        {/* Results List */}
        <div ref={scrollContainerRef} className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
          {loading && customers.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto mb-4"></div>
              <p className="text-gray-500">Searching...</p>
            </div>
          ) : customers.length > 0 ? (
            <div className="space-y-2">
              {customers.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => onCustomerSelect(customer)}
                  className="w-full text-left p-4 rounded-lg border-2 border-gray-200 bg-white transition-all duration-200 hover:bg-blue-50 hover:border-blue-300"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">
                        {customer.name || 'Unnamed Customer'}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        {customer.company_name && (
                          <span className="text-sm text-gray-600 flex items-center gap-1">
                            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{customer.company_name}</span>
                          </span>
                        )}
                        {customer.email && (
                          <span className="text-sm text-gray-600 flex items-center gap-1">
                            <Mail size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{customer.email}</span>
                          </span>
                        )}
                        {formatAddress(customer) && (
                          <span className="text-sm text-gray-600 flex items-center gap-1">
                            <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{formatAddress(customer)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}

              {/* Sentinel element for infinite scroll */}
              <div ref={sentinelRef} className="h-4" />

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pest-blue mx-auto mb-2"></div>
                  <p className="text-gray-400 text-sm">Loading more...</p>
                </div>
              )}

              {/* End of results */}
              {!hasMore && customers.length >= ITEMS_PER_PAGE && (
                <p className="text-center text-gray-400 text-sm py-2">
                  All {totalCount} results shown
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">No customers found</p>
              <p className="text-gray-400 text-sm mb-4">
                {searchTerm.trim() ? 'Try adjusting your search terms' : 'No customers available'}
              </p>
              <button
                onClick={onAddNew}
                className="inline-flex items-center gap-2 px-4 py-2 bg-pest-blue hover:bg-pest-teal text-white rounded-lg transition-colors duration-200 text-sm font-medium"
              >
                <UserPlus size={18} />
                Add New Customer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSearchModal;
